<template>
  <div class="vb-detail" :class="{ 'vb-detail--only-stay': is_only_stay }">
    <b-alert v-if="no_data" show variant="warning">
      Nessun dato disponibile per questa struttura.
    </b-alert>

    <div v-else>
      <div v-if="no_solutions">
        <b-alert show variant="warning">
          Attenzione, non ci sono soluzioni disponibili per questo pacchetto.
          Torna indietro o prova a cambiare i criteri di ricerca.
        </b-alert>
        <br /><br />
      </div>

      <detail-header :header="header" />

      <div
        v-for="(block, index) in blocks"
        :key="`${_uid}:${block.component}:${index}`"
      >
        <hr v-if="block.divided" class="section vb-detail__divider" />
        <component
          :is="block.component"
          v-bind="block.props"
          @selected-room="on_solution_selected"
          @scroll-to-recap="scroll_into_view"
        />
      </div>

      <!--    RECAP PACCHETTO -->
      <hr v-if="is_only_stay && recap" class="section vb-detail__divider" />
      <card-recap
        v-if="recap"
        class="section vb-detail__recap"
        :recap="recap"
      />

      <!-- CTA continua -->
      <room-select-button
        ref="room_select_button"
        :disabled="!recap"
        :show_locked="show_locked"
      />
    </div>
  </div>
</template>

<script>
  import { mapState } from 'vuex';
  import {
    alpitour_rybbit_event,
    slim_room_info,
  } from '@alpitour/utilities/rybbit';
  import {
    flight_and_stay_blocks,
    flight_and_stay_header,
    only_stay_blocks,
    only_stay_header,
  } from '@alpitour/utilities/detail-blocks';

  import DetailHeader from '@alpitour/components/blocks/detail/header.vue';
  import Gallery from '@/components/blocks/images/gallery.vue';
  import Transfer from '@alpitour/components/blocks/detail/transfer.vue';
  import PanoramicHotel from '@alpitour/components/blocks/detail/panoramic-hotel.vue';
  import PanoramicTour from '@alpitour/components/blocks/detail/panoramic-tour.vue';
  import Description from '@alpitour/components/blocks/detail/description.vue';
  import TabbedInfo from '@alpitour/components/blocks/detail/tabbed-info.vue';
  import Accommodations from '@alpitour/components/blocks/detail/accommodations.vue';
  import Rooms from '@alpitour/components/blocks/detail/rooms.vue';
  import Solutions from '@alpitour/components/blocks/detail/solutions.vue';
  import RatingTour from '@alpitour/components/blocks/detail/rating-tour.vue';
  import Program from '@alpitour/components/blocks/detail/program.vue';
  import ServicesInsurance from '@alpitour/components/blocks/detail/assicurazione-medico-bagaglio.vue';
  import ServicesPopular from '@alpitour/components/blocks/detail/services-popular.vue';
  import ServicesAll from '@alpitour/components/blocks/detail/services-all.vue';
  import Faq from '@alpitour/components/blocks/detail/faq.vue';
  import Promo from '@alpitour/components/blocks/detail/promo.vue';
  import CardRecap from '@alpitour/components/blocks/card-recap.vue';
  import RoomSelectButton from '@alpitour/components/blocks/detail/room-select-button.vue';

  export default {
    name: 'detail-page',

    components: {
      'detail-header': DetailHeader,
      gallery: Gallery,
      transfer: Transfer,
      'panoramic-hotel': PanoramicHotel,
      'panoramic-tour': PanoramicTour,
      description: Description,
      'tabbed-info': TabbedInfo,
      accommodations: Accommodations,
      rooms: Rooms,
      solutions: Solutions,
      'rating-tour': RatingTour,
      program: Program,
      'services-insurance': ServicesInsurance,
      'services-popular': ServicesPopular,
      'services-all': ServicesAll,
      faq: Faq,
      promo: Promo,
      'card-recap': CardRecap,
      'room-select-button': RoomSelectButton,
    },

    provide() {
      return {
        on_room_selected: this.on_room_selected,
        room_selection: this.room_selection,
      };
    },

    props: {
      is_only_stay: {
        type: Boolean,
        default: false,
      },
      hotel_data: {
        type: Object,
        default: () => ({}),
      },
      product_detail: {
        type: Array,
        default: () => [],
      },
      statics_detail: {
        type: Object,
        default: () => ({}),
      },
      supplement: {
        type: Array,
        default: () => [],
      },
      product_brand: {
        type: String,
        default: '',
      },
    },

    data() {
      return {
        room_selection: { index: null, rate: null },
        selected_room: {},
      };
    },

    computed: {
      ...mapState('liveat', ['detail_params', 'solution_id']),

      hotel() {
        const { data } = this.hotel_data;
        return data || null;
      },

      product() {
        const [product] = this.product_detail;
        return product || null;
      },

      no_data() {
        return this.is_only_stay && !this.hotel;
      },

      no_solutions() {
        if (this.is_only_stay || !this.product) return false;
        const { soluzioni_parsed = [] } = this.product;
        return soluzioni_parsed.length === 0;
      },

      show_locked() {
        return !this.no_solutions;
      },

      header() {
        if (this.is_only_stay) return only_stay_header(this.hotel || {});
        return flight_and_stay_header(this.product || {}, this.product_brand);
      },

      blocks() {
        if (this.is_only_stay) return only_stay_blocks(this.hotel_data);

        const { product_detail, statics_detail, supplement } = this;
        return flight_and_stay_blocks({
          product_detail,
          statics_detail,
          supplement,
        });
      },

      recap() {
        const { recap } = this.selected_room;
        return recap || null;
      },
    },

    updated() {
      if (this.is_only_stay && this.room_selection.index !== null) {
        this.scroll_into_view();
      }
    },

    methods: {
      // flight-and-stay: solutions.vue committa gia` store e analytics,
      // qui serve solo il recap per la card e per sbloccare la CTA.
      on_solution_selected(room) {
        this.selected_room = room;
      },

      on_room_selected(payload) {
        this.room_selection.index = payload.room_index;
        this.room_selection.rate = payload.rate;

        const rate = payload.rate || {};
        const { solution: rate_solution = {}, pricing = {} } = rate;
        const {
          checkin = '',
          checkout = '',
          occupancy = [],
        } = this.detail_params || {};
        const { adults = 0, children = [] } = occupancy[0] || {};
        const { name: room_name = '' } = payload.room || {};
        const nights =
          checkin && checkout
            ? Math.round((new Date(checkout) - new Date(checkin)) / 86400000)
            : 0;

        const selected_room = {
          room_index: payload.room_index,
          rate,

          quote_payload: {
            solution: {
              id: rate_solution.id || this.solution_id,
              room_ref: rate_solution.room_ref,
            },
            locale: 'IT',
            totAmount: pricing.total || 0,
          },

          // informazioni per card-recap
          recap: {
            checkin,
            checkout,
            nights,
            adults,
            children: children.length,
            infants: 0,
            room_name,
            board_name: rate.description || '',
            total_amount: pricing.total || 0,
          },

          solution: {
            idSolution: rate_solution.id || this.solution_id,
            roomRef: rate_solution.room_ref,
            dateFrom: checkin,
            dateTo: checkout,
            nights,
          },
        };

        this.selected_room = selected_room;
        this.$store.commit('liveat/update_selected_room', selected_room);
        alpitour_rybbit_event(
          'package:room:select',
          slim_room_info(selected_room)
        );
      },

      scroll_into_view() {
        const { room_select_button } = this.$refs;
        if (room_select_button && room_select_button.scroll_into_view) {
          room_select_button.scroll_into_view();
        }
      },
    },
  };
</script>

<style lang="scss">
  .vb-detail {
    margin-bottom: $grid-gutter-width * 4;

    &--only-stay .information {
      border-top-color: transparent;
    }

    &__divider {
      border-color: $gray-400;
    }

    // blocchi condivisi con altri siti: gli override restano confinati al dettaglio
    section.gallery {
      margin-top: 0;
    }

    .gallery .gallery__main {
      @include media-breakpoint-up(md) {
        grid-template-columns: repeat(2, 1.14fr) repeat(2, 1fr) 1.15fr;
      }
    }

    .vb-btn.btn-lg {
      width: 150px;
      height: 40px;
      padding: 7px 0;
      font-size: $font-size-base;
    }

    .vb-tabs {
      .vb-tabs__content {
        border-radius: $border-radius-lg * 2;
      }

      .solution__tabs-content {
        padding-top: 1rem !important;
      }

      .vb-tabs__nav .active {
        border-radius: $border-radius-lg * 2 $border-radius-lg * 2 0 0;
      }

      &:not(.vb-tabs--vertical) .nav-link {
        padding-top: 13px;
        padding-bottom: 13px;

        &.active {
          padding-left: 25px;
        }
      }
    }

    .vb-tabs--vertical {
      .vb-tabs__nav a.nav-link {
        display: flex;
        border-radius: $border-radius-lg * 2;
      }

      @include media-breakpoint-up(lg) {
        // 296px di tab + 15px di stacco dal pannello
        > :first-child {
          flex: 0 0 311px;
          max-width: 311px;
        }
      }
    }

    // Badge no-passaporto
    &__badge {
      cursor: default;

      &--passport {
        display: flex;
        align-items: center;
        margin-left: 0.625rem;
      }

      &-icon {
        margin-left: 0.188rem;
      }
    }

    // Back Link
    &__back {
      position: relative;
      display: flex;
      align-items: center;
      cursor: pointer;
      color: $info;

      &:before {
        content: '';
        width: 0.5rem;
        height: 0.5rem;
        border-top: 1px solid $info;
        border-left: 1px solid $info;
        transform: rotate(-45deg);
        margin-right: 3px;
      }
    }

    // Titolo Struttura
    &__head {
      @include media-breakpoint-up(lg) {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
      }

      .vb-icon {
        &--heart {
          position: absolute;
          right: 0;
          top: 10px;
          cursor: pointer;
          fill: transparent;
          stroke: $gray-600;

          &:hover {
            stroke: $red;
            fill: $red;
          }
        }
      }

      &-star {
        display: flex;
      }

      &-city {
        display: flex;
        align-items: center;
        color: $gray-600;
        margin-right: $grid-gutter-width/2;
      }

      &-rate {
        display: flex;
        align-items: center;
        color: $gray-600;
        white-space: nowrap;

        img {
          width: 40px;
        }
      }

      &-brand {
        img,
        svg {
          height: 16px;
          width: auto;

          @include media-breakpoint-up(lg) {
            height: 20px;
          }
        }
      }
    }
  }
</style>
